/**
 * Wiederholsichere Lagerbuchungen (Client-Seite von P12): „Buchen“ friert die
 * Erfassungsliste als unveränderlichen Buchungsauftrag mit dauerhafter
 * Vorgangskennung (`vorgangId`) ein und legt ihn zuerst in IndexedDB ab; erst danach
 * wird die Liste geleert. Eine Warteschlange sendet ausstehende Aufträge – auch nach
 * Neustart, Sitzungsende oder Verbindungsabbruch – immer mit derselben Kennung und
 * demselben Inhalt. Hat der Server schon gebucht und nur die Antwort ging verloren,
 * liefert er das gespeicherte Ergebnis statt doppelt zu buchen.
 *
 * Vertrag mit dem Server (POST …/lager/erfassung, Serverseite Agent 1):
 *  - gleiche `vorgangId` + gleicher Inhalt → gespeichertes Ergebnis (`wiederholt: true`),
 *    keine neuen Bewegungen;
 *  - gleiche `vorgangId` + anderer Inhalt → 409 (Konflikt, wird nie automatisch gelöst);
 *  - Bewegungen und Vorgangsergebnis entstehen in einer Transaktion: eine Fehlerantwort
 *    (4xx/5xx) bedeutet „nichts gebucht“, eine Wiederholung mit derselben Kennung ist
 *    immer sicher.
 *
 * Zustände: ausstehend (wartet auf Senden, mit Grund) → gebucht | Konflikt.
 */
import { ApiFehler, type ErfassungErgebnis, type ErfassungPosition } from './api';
import { neueVorgangsId } from './bonierung';
import type { Erfassung, ErfassungsArt, LaufAnker } from './lager';

export type AuftragsStatus = 'ausstehend' | 'gebucht' | 'konflikt';

/** Warum ein ausstehender Auftrag gerade nicht automatisch gesendet wird. */
export type Wartegrund =
  /** Kein Netz, Server nicht erreichbar oder überlastet – automatisch erneut. */
  | 'verbindung'
  /** Geräteschlüssel ungültig oder widerrufen – erst nach neuer Einrichtung. */
  | 'zugang'
  /** Anmeldung bzw. Recht fehlt (403) – nach Bedieneranmeldung erneut. */
  | 'recht'
  /** Inventur ist älter als {@link INVENTUR_AUTOMATIK_MS} – nur nach Bestätigung senden. */
  | 'pruefen';

/** Unveränderlicher Inhalt: genau das wird bei jedem Versuch gesendet. */
export interface AuftragsInhalt {
  /** Dauerhafte Vorgangskennung = Schlüssel des Auftrags. */
  readonly vorgangId: string;
  /** Ablagebereich, z. B. `kasse:K1` oder `unternehmer:U1` (Handterminal). */
  readonly kontext: string;
  readonly art: ErfassungsArt;
  readonly bezug: string;
  readonly positionen: readonly Readonly<ErfassungPosition>[];
  /** Zeitpunkt von „Buchen“ (ISO). */
  readonly erstelltAm: string;
  /** Wer gebucht hat (nur Anzeige). */
  readonly bediener: string | null;
  /**
   * Inventur im offenen Inventurlauf: Ziel ist die Zählung dieses Laufs (Zählstelle = bezug)
   * statt der freien Erfassung. Fehlt bei allen anderen Aufträgen.
   */
  readonly laufId?: number;
  /** Laufanker der Inventurliste (Server prüft damit, ob die Liste über einen Lauf hinweg geht). */
  readonly laufAnker?: Readonly<LaufAnker>;
}

export interface AuftragsZustand {
  status: AuftragsStatus;
  wartegrund: Wartegrund | null;
  versuche: number;
  letzterVersuch: string | null;
  /** Frühester nächster automatischer Versuch (ISO); null = sofort. */
  naechsterVersuch: string | null;
  /** Letzte Fehlermeldung bzw. Konfliktgrund. */
  meldung: string | null;
  ergebnisse: ErfassungErgebnis[] | null;
  gebuchtAm: string | null;
  /** Der Server hat den Vorgang als Wiederholung erkannt (Antwort war verloren). */
  wiederholt: boolean;
  /** Nicht gebuchte Zeilen wurden schon in die Erfassungsliste zurückgeholt. */
  fehlerUebernommen?: boolean;
  /**
   * Art des Konflikts: `inhalt` = 409, unter der Kennung liegt am Server ein anderer
   * Inhalt (der kann gebucht sein!); `abgewiesen` = der Server hat nichts gebucht.
   */
  konfliktArt?: 'inhalt' | 'abgewiesen' | 'unklar';
  /**
   * Ein früherer Versuch endete ohne eindeutige Antwort (Netz, Zeitüberschreitung, 5xx):
   * der Server kann gebucht haben. Eine spätere Abweisung beweist dann nicht „nichts gebucht“.
   */
  unklarGesendet?: boolean;
  /** Vor jedem Versuch gesetzt, danach gelöscht – bleibt nur bei Absturz mitten im Versuch. */
  versuchLaeuft?: boolean;
}

export interface Buchungsauftrag {
  readonly inhalt: AuftragsInhalt;
  zustand: AuftragsZustand;
}

/** Körper für POST …/lager/erfassung – aus dem eingefrorenen Inhalt, bei jedem Versuch gleich. */
export interface ErfassungsKoerper {
  vorgangId: string;
  art: ErfassungsArt;
  positionen: ErfassungPosition[];
  bezug?: string;
  /** Nur Inventur im Lauf: der Sender schickt den Auftrag an die Zählung dieses Laufs. */
  laufId?: number;
  /** Nur Inventur: Laufanker der Liste. */
  laufAnker?: LaufAnker;
}

export interface ErfassungsAntwort {
  ergebnisse: ErfassungErgebnis[];
  wiederholt?: boolean;
}

/** Sendet einen Auftrag an den Server (Kasse oder Lagergerät). */
export type Sender = (koerper: ErfassungsKoerper) => Promise<ErfassungsAntwort>;

/** Ablage der Aufträge (IndexedDB, im Test der Speicher). */
export interface AuftragsSpeicher {
  /** Aufträge des Kontexts, älteste zuerst. */
  alle(kontext: string): Promise<Buchungsauftrag[]>;
  /** Neu ablegen; wirft, wenn die Kennung schon existiert. Erfüllt erst nach dem Speichern. */
  lege(a: Buchungsauftrag): Promise<void>;
  /** Nur den Zustand ersetzen – der Inhalt bleibt, wie er ist. */
  setzeZustand(vorgangId: string, zustand: AuftragsZustand): Promise<void>;
  entferne(vorgangId: string): Promise<void>;
}

/**
 * Abweisungen wegen des Laufstands (nichts gespeichert): Zählung an einen nicht (mehr) offenen
 * Lauf (InventurKonflikt) bzw. freie Inventur, während ein Lauf offen ist.
 */
const LAUF_NICHT_OFFEN =
  /^Inventurlauf \d+ (ist nicht mehr offen|ist nicht offen|unbekannt|ist offen)/;

/** Inventuren setzen den Bestand absolut – nach so langer Zeit nur noch nach Bestätigung senden. */
export const INVENTUR_AUTOMATIK_MS = 30 * 60_000;
/** Gebuchte Aufträge bleiben so lange zur Anzeige stehen. */
export const GEBUCHT_AUFBEWAHREN_MS = 3 * 86_400_000;
const MAX_WARTEZEIT_MS = 5 * 60_000;

export function koerperAus(inhalt: AuftragsInhalt): ErfassungsKoerper {
  return {
    vorgangId: inhalt.vorgangId,
    art: inhalt.art,
    positionen: inhalt.positionen.map((p) => ({ ...p })),
    ...(inhalt.bezug === '' ? {} : { bezug: inhalt.bezug }),
    ...(inhalt.laufId === undefined ? {} : { laufId: inhalt.laufId }),
    ...(inhalt.laufAnker === undefined ? {} : { laufAnker: { ...inhalt.laufAnker } }),
  };
}

function friere<T extends object>(x: T): Readonly<T> {
  return Object.freeze(x);
}

const NEUER_ZUSTAND: AuftragsZustand = {
  status: 'ausstehend',
  wartegrund: null,
  versuche: 0,
  letzterVersuch: null,
  naechsterVersuch: null,
  meldung: null,
  ergebnisse: null,
  gebuchtAm: null,
  wiederholt: false,
};

/**
 * Auftrag aus der Erfassungsliste: genau die Zeilen der Liste (unbekannte Codes mit
 * Bezeichnung, wenn eine eingetragen ist). Kennung: `vorhandeneId` (alte Sitzungskennung,
 * die schon an den Server ging), sonst die Kennung der Liste, sonst eine neue.
 */
export function erstelleAuftrag(
  erfassung: Erfassung,
  kontext: string,
  jetzt: Date,
  bediener: string | null,
  neueId: () => string = neueVorgangsId,
  vorhandeneId: string | null = null,
  /** Offener Inventurlauf (nur bei Inventur): Zählung im Lauf statt freier Erfassung. */
  laufId: number | null = null,
): Buchungsauftrag {
  if (erfassung.zeilen.length === 0) {
    throw new Error('Die Liste ist leer');
  }
  const imLauf = laufId !== null && erfassung.art === 'inventur';
  if (imLauf && erfassung.bezug.trim() === '') {
    throw new Error(
      'Zählstelle angeben (z. B. Regal 3) – im Inventurlauf zählt jede Stelle für sich',
    );
  }
  const positionen = erfassung.zeilen.map((z) =>
    friere({
      code: z.code,
      menge: z.menge,
      ...(z.bekannt || z.bezeichnung.trim() === '' ? {} : { bezeichnung: z.bezeichnung.trim() }),
    }),
  );
  return {
    inhalt: friere({
      vorgangId: vorhandeneId ?? erfassung.vorgangId ?? neueId(),
      kontext,
      art: erfassung.art,
      bezug: erfassung.bezug, // unverändert wie bisher gesendet (alte Kennungen bleiben gültig)
      positionen: friere(positionen),
      erstelltAm: jetzt.toISOString(),
      bediener,
      ...(imLauf ? { laufId } : {}),
      ...(erfassung.art === 'inventur' && erfassung.laufAnker !== undefined
        ? { laufAnker: friere({ ...erfassung.laufAnker }) }
        : {}),
    }),
    zustand: { ...NEUER_ZUSTAND },
  };
}

/** Inhalt als Text (für die Übernahme einer alten Sitzungskennung, siehe lager.ts). */
export function inhaltText(
  art: ErfassungsArt,
  bezug: string,
  positionen: readonly unknown[],
): string {
  return JSON.stringify([art, bezug, positionen]);
}

function wartezeitMs(versuche: number): number {
  return Math.min(MAX_WARTEZEIT_MS, 5_000 * 2 ** Math.max(0, versuche - 1));
}

/** Ergebnis eines Sendeversuchs als neuer Zustand (reine Funktion). */
export function zustandNachVersuch(
  alt: AuftragsZustand,
  ergebnis: { ok: ErfassungsAntwort } | { fehler: unknown },
  jetzt: Date,
): AuftragsZustand {
  const versuche = alt.versuche + 1;
  const basis = { ...alt, versuche, letzterVersuch: jetzt.toISOString() };
  if ('ok' in ergebnis) {
    return {
      ...basis,
      status: 'gebucht',
      wartegrund: null,
      naechsterVersuch: null,
      meldung: null,
      ergebnisse: ergebnis.ok.ergebnisse,
      gebuchtAm: jetzt.toISOString(),
      wiederholt: ergebnis.ok.wiederholt === true,
    };
  }
  const e = ergebnis.fehler;
  const text = e instanceof Error ? e.message : String(e);
  const spaeter = (grund: Wartegrund, ms: number | null): AuftragsZustand => ({
    ...basis,
    status: 'ausstehend',
    wartegrund: grund,
    meldung: text,
    naechsterVersuch: ms === null ? null : new Date(jetzt.getTime() + ms).toISOString(),
  });
  if (!(e instanceof ApiFehler)) {
    // Netzfehler, Abbruch, Zeitüberschreitung: Ausgang unbekannt
    return { ...spaeter('verbindung', wartezeitMs(versuche)), unklarGesendet: true };
  }
  if (e.status === 409 && LAUF_NICHT_OFFEN.test(text)) {
    // Zählung an einen Lauf, der inzwischen abgeschlossen/verworfen ist: der Server hat in
    // derselben Transaktion nichts gespeichert (eine frühere erfolgreiche Zählung käme als
    // Wiederholung zurück) – abgewiesen, nicht „anderer Inhalt“.
    return {
      ...basis,
      status: 'konflikt',
      konfliktArt: 'abgewiesen',
      wartegrund: null,
      naechsterVersuch: null,
      meldung: text,
    };
  }
  if (e.status === 409) {
    return {
      ...basis,
      status: 'konflikt',
      konfliktArt: 'inhalt',
      wartegrund: null,
      naechsterVersuch: null,
      meldung: text,
    };
  }
  if (e.status === 401 || e.status === 0) {
    return spaeter('zugang', null); // Schlüssel ungültig/widerrufen bzw. Gerät nicht eingerichtet
  }
  if (e.status === 403) {
    return spaeter('recht', null);
  }
  if (e.status === 429) {
    return spaeter('verbindung', wartezeitMs(versuche)); // vor der Verarbeitung abgewiesen
  }
  if (e.status >= 500 || e.status === 408) {
    return { ...spaeter('verbindung', wartezeitMs(versuche)), unklarGesendet: true };
  }
  // 400, 404, 422 …: Der Server weist den Inhalt ab; ein erneuter Versuch hilft nicht.
  return {
    ...basis,
    status: 'konflikt',
    konfliktArt: alt.unklarGesendet === true ? 'unklar' : 'abgewiesen',
    wartegrund: null,
    naechsterVersuch: null,
    meldung: `Abgewiesen (${String(e.status)}): ${text}`,
  };
}

/** Darf die Warteschlange diesen Auftrag jetzt ohne Rückfrage senden? */
export function automatischSendbar(a: Buchungsauftrag, jetzt: Date): boolean {
  const z = a.zustand;
  if (z.status !== 'ausstehend') {
    return false;
  }
  if (z.wartegrund === 'zugang' || z.wartegrund === 'recht' || z.wartegrund === 'pruefen') {
    return false;
  }
  if (z.naechsterVersuch !== null && new Date(z.naechsterVersuch).getTime() > jetzt.getTime()) {
    return false;
  }
  return !inventurZuAlt(a, jetzt);
}

export function inventurZuAlt(a: Buchungsauftrag, jetzt: Date): boolean {
  return (
    a.inhalt.art === 'inventur' &&
    jetzt.getTime() - new Date(a.inhalt.erstelltAm).getTime() > INVENTUR_AUTOMATIK_MS
  );
}

export interface LaufErgebnis {
  gesendet: number;
  gebucht: number;
  konflikte: number;
  /** Lauf angehalten (Zugang/Recht/Verbindung) – spätere Aufträge warten in Reihenfolge. */
  angehalten: Wartegrund | null;
}

/**
 * Ausstehende Aufträge in Reihenfolge senden. Ein Auftrag, der warten muss, hält alle
 * späteren auf – eine Inventur darf einen früheren Wareneingang nicht überholen.
 * Konflikte blockieren nicht (sie haben nichts gebucht).
 *
 * `manuell` sendet zusätzlich genau diesen Auftrag, auch wenn er auf Bestätigung oder
 * Anmeldung wartet (Taste „Jetzt senden“).
 */
export async function verarbeiteWarteschlange(
  speicher: AuftragsSpeicher,
  kontext: string,
  sende: Sender,
  jetzt: () => Date,
  manuell: string | null = null,
  /** Lauf beenden, bevor der nächste Auftrag gesendet wird (Abmeldung, Gerätewechsel). */
  abgebrochen: () => boolean = () => false,
): Promise<LaufErgebnis> {
  const ergebnis: LaufErgebnis = { gesendet: 0, gebucht: 0, konflikte: 0, angehalten: null };
  const liste = await speicher.alle(kontext);
  for (const a of liste) {
    if (a.zustand.status !== 'ausstehend' || a.inhalt.kontext !== kontext) {
      continue;
    }
    if (abgebrochen()) {
      break;
    }
    const istManuell = a.inhalt.vorgangId === manuell;
    if (!istManuell && !automatischSendbar(a, jetzt())) {
      if (inventurZuAlt(a, jetzt()) && a.zustand.wartegrund === null) {
        await speicher.setzeZustand(a.inhalt.vorgangId, {
          ...a.zustand,
          wartegrund: 'pruefen',
          meldung: 'Inventur ist älter als 30 Minuten – vor dem Senden prüfen',
        });
      }
      ergebnis.angehalten = a.zustand.wartegrund ?? 'pruefen';
      break;
    }
    // Vor dem Senden dauerhaft vermerken, dass ein Versuch läuft. Findet ein späterer Lauf
    // diesen Vermerk noch vor, ist der Tab mitten im Versuch gestorben – der Server kann
    // gebucht haben (unklar), eine spätere Abweisung beweist dann nichts.
    const vorher: AuftragsZustand =
      a.zustand.versuchLaeuft === true ? { ...a.zustand, unklarGesendet: true } : a.zustand;
    await speicher.setzeZustand(a.inhalt.vorgangId, { ...vorher, versuchLaeuft: true });
    let neu: AuftragsZustand;
    try {
      const antwort = await sende(koerperAus(a.inhalt));
      neu = zustandNachVersuch(vorher, { ok: antwort }, jetzt());
    } catch (e: unknown) {
      neu = zustandNachVersuch(vorher, { fehler: e }, jetzt());
    }
    neu = { ...neu, versuchLaeuft: false };
    await speicher.setzeZustand(a.inhalt.vorgangId, neu);
    ergebnis.gesendet++;
    if (neu.status === 'gebucht') {
      ergebnis.gebucht++;
    } else if (neu.status === 'konflikt') {
      ergebnis.konflikte++;
    } else {
      ergebnis.angehalten = neu.wartegrund;
      break;
    }
  }
  return ergebnis;
}

/** Wartegründe „zugang“/„recht“ nach neuer Einrichtung bzw. Anmeldung zurücksetzen. */
export async function gibFrei(
  speicher: AuftragsSpeicher,
  kontext: string,
  gruende: readonly Wartegrund[],
): Promise<void> {
  for (const a of await speicher.alle(kontext)) {
    if (
      a.zustand.status === 'ausstehend' &&
      a.zustand.wartegrund !== null &&
      gruende.includes(a.zustand.wartegrund)
    ) {
      await speicher.setzeZustand(a.inhalt.vorgangId, {
        ...a.zustand,
        wartegrund: null,
        naechsterVersuch: null,
      });
    }
  }
}

/** Alte gebuchte Aufträge entfernen (Konflikte und Ausstehende bleiben immer). */
export async function raeumeAuf(
  speicher: AuftragsSpeicher,
  kontext: string,
  jetzt: Date,
): Promise<void> {
  for (const a of await speicher.alle(kontext)) {
    if (
      a.zustand.status === 'gebucht' &&
      a.zustand.gebuchtAm !== null &&
      jetzt.getTime() - new Date(a.zustand.gebuchtAm).getTime() > GEBUCHT_AUFBEWAHREN_MS
    ) {
      await speicher.entferne(a.inhalt.vorgangId);
    }
  }
}

/** Konflikt bewusst verwerfen (nach Prüfung der Bewegungshistorie). Andere Zustände nie. */
export async function verwirfKonflikt(
  speicher: AuftragsSpeicher,
  a: Buchungsauftrag,
): Promise<void> {
  if (a.zustand.status !== 'konflikt') {
    throw new Error('Nur Konflikte können verworfen werden');
  }
  await speicher.entferne(a.inhalt.vorgangId);
}

/** Fehlerzeilen eines gebuchten Auftrags (der Server hat sie nicht gebucht). */
export function fehlerzeilen(
  a: Buchungsauftrag,
): { code: string; menge: number; fehler: string }[] {
  const fehler = (a.zustand.ergebnisse ?? []).filter(
    (e): e is Extract<ErfassungErgebnis, { ergebnis: 'fehler' }> => e.ergebnis === 'fehler',
  );
  const genutzt = new Set<number>();
  return fehler.map((f) => {
    // Gleicher Code kann mehrfach vorkommen: die erste noch nicht zugeordnete Position nehmen.
    const i = a.inhalt.positionen.findIndex(
      (p, j) => !genutzt.has(j) && (p.code === f.code || normal(p.code) === f.code),
    );
    genutzt.add(i);
    return { code: f.code, menge: a.inhalt.positionen[i]?.menge ?? 0, fehler: f.fehler };
  });
}

function normal(code: string): string {
  return code.replace(/\s+/g, '');
}

// ---------------------------------------------------------------- Ablage

function kopie(a: Buchungsauftrag): Buchungsauftrag {
  return { inhalt: a.inhalt, zustand: { ...a.zustand } };
}

function nachZeit(a: Buchungsauftrag, b: Buchungsauftrag): number {
  return (
    a.inhalt.erstelltAm.localeCompare(b.inhalt.erstelltAm) ||
    a.inhalt.vorgangId.localeCompare(b.inhalt.vorgangId)
  );
}

/** Ablage im Arbeitsspeicher (Tests, Rückfall ohne IndexedDB). */
export class SpeicherAuftraege implements AuftragsSpeicher {
  private readonly eintraege = new Map<string, Buchungsauftrag>();

  alle(kontext: string): Promise<Buchungsauftrag[]> {
    return Promise.resolve(
      [...this.eintraege.values()]
        .filter((a) => a.inhalt.kontext === kontext)
        .sort(nachZeit)
        .map(kopie),
    );
  }

  lege(a: Buchungsauftrag): Promise<void> {
    if (this.eintraege.has(a.inhalt.vorgangId)) {
      return Promise.reject(new Error(`Vorgang ${a.inhalt.vorgangId} gibt es schon`));
    }
    this.eintraege.set(a.inhalt.vorgangId, kopie(a));
    return Promise.resolve();
  }

  setzeZustand(vorgangId: string, zustand: AuftragsZustand): Promise<void> {
    const a = this.eintraege.get(vorgangId);
    if (a !== undefined) {
      this.eintraege.set(vorgangId, { inhalt: a.inhalt, zustand: { ...zustand } });
    }
    return Promise.resolve();
  }

  entferne(vorgangId: string): Promise<void> {
    this.eintraege.delete(vorgangId);
    return Promise.resolve();
  }
}

const DB_NAME = 'bilanx-lager';
const STORE = 'buchungsauftraege';

function anfrage<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((loese, lehneAb) => {
    req.onsuccess = () => {
      loese(req.result);
    };
    req.onerror = () => {
      lehneAb(req.error ?? new Error('IndexedDB-Fehler'));
    };
  });
}

function abgeschlossen(tx: IDBTransaction): Promise<void> {
  return new Promise((loese, lehneAb) => {
    tx.oncomplete = () => {
      loese();
    };
    tx.onerror = () => {
      lehneAb(tx.error ?? new Error('IndexedDB-Transaktion fehlgeschlagen'));
    };
    tx.onabort = () => {
      lehneAb(tx.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
    };
  });
}

interface Datensatz {
  vorgangId: string;
  kontext: string;
  inhalt: AuftragsInhalt;
  zustand: AuftragsZustand;
}

/**
 * Ablage in IndexedDB (eigene Datenbank `bilanx-lager`): überlebt Neustart, Tab-Ende und
 * Sitzungsende. Schlüssel ist die Vorgangskennung; `lege` nutzt `add`, eine doppelte
 * Kennung scheitert also. Der Inhalt wird beim Zustandswechsel unverändert zurückgeschrieben.
 */
export class IndexedDbAuftraege implements AuftragsSpeicher {
  private db: Promise<IDBDatabase> | null = null;

  constructor(private readonly indexedDb: IDBFactory) {}

  private oeffne(): Promise<IDBDatabase> {
    this.db ??= new Promise((loese, lehneAb) => {
      const req = this.indexedDb.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const s = req.result.createObjectStore(STORE, { keyPath: 'vorgangId' });
        s.createIndex('kontext', 'kontext');
      };
      req.onsuccess = () => {
        loese(req.result);
      };
      req.onerror = () => {
        lehneAb(req.error ?? new Error('IndexedDB nicht verfügbar'));
      };
    });
    return this.db;
  }

  async alle(kontext: string): Promise<Buchungsauftrag[]> {
    const db = await this.oeffne();
    const s = db.transaction(STORE, 'readonly').objectStore(STORE);
    const roh = await anfrage(s.index('kontext').getAll(kontext) as IDBRequest<Datensatz[]>);
    return roh.map((d) => ({ inhalt: friere(d.inhalt), zustand: d.zustand })).sort(nachZeit);
  }

  async lege(a: Buchungsauftrag): Promise<void> {
    const db = await this.oeffne();
    const tx = db.transaction(STORE, 'readwrite');
    const fertig = abgeschlossen(tx);
    const d: Datensatz = {
      vorgangId: a.inhalt.vorgangId,
      kontext: a.inhalt.kontext,
      inhalt: a.inhalt,
      zustand: a.zustand,
    };
    tx.objectStore(STORE).add(d);
    await fertig;
  }

  async setzeZustand(vorgangId: string, zustand: AuftragsZustand): Promise<void> {
    const db = await this.oeffne();
    const tx = db.transaction(STORE, 'readwrite');
    const fertig = abgeschlossen(tx);
    const s = tx.objectStore(STORE);
    const req = s.get(vorgangId) as IDBRequest<Datensatz | undefined>;
    req.onsuccess = () => {
      const d = req.result;
      if (d !== undefined) {
        s.put({ ...d, zustand });
      }
    };
    await fertig;
  }

  async entferne(vorgangId: string): Promise<void> {
    const db = await this.oeffne();
    const tx = db.transaction(STORE, 'readwrite');
    const fertig = abgeschlossen(tx);
    tx.objectStore(STORE).delete(vorgangId);
    await fertig;
  }
}

/** Ablage des Geräts: IndexedDB, sonst (sehr alte Browser) nur im Arbeitsspeicher. */
export function auftragsSpeicherFuerGeraet(): { speicher: AuftragsSpeicher; dauerhaft: boolean } {
  if ('indexedDB' in globalThis) {
    return { speicher: new IndexedDbAuftraege(globalThis.indexedDB), dauerhaft: true };
  }
  return { speicher: new SpeicherAuftraege(), dauerhaft: false };
}

/**
 * Nur ein Lauf je Kontext gleichzeitig – im Tab über eine Promise-Kette, über Tabs hinweg
 * mit der Web-Locks-API (Chrome/Android), falls vorhanden. Doppelte Läufe wären dank
 * Vorgangskennung zwar harmlos, kosten aber Anfragen und verwirren die Anzeige.
 */
const laeufe = new Map<string, Promise<unknown>>();
export function exklusiv<T>(kontext: string, arbeit: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as { locks?: LockManager } | undefined)?.locks;
  const mitSperre = (): Promise<T> =>
    locks === undefined ? arbeit() : locks.request(`bilanx-lager-${kontext}`, arbeit);
  const vorher = laeufe.get(kontext) ?? Promise.resolve();
  const lauf = vorher.then(mitSperre, mitSperre);
  laeufe.set(
    kontext,
    lauf.catch(() => undefined),
  );
  return lauf;
}
